import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Input, List, Pagination, Typography, Empty } from 'antd';
import { useHistory } from 'react-router-dom';
import './ArticleList.less';

export type ArticleListItem = {
  id: number;
  title: string;
  content: string;
};

type Props = {
  dataId: string;
  data: ArticleListItem[];
  pageSize?: number;
  showSearch?: boolean;
  emptyText?: string;
};

type NormalizedItem = ArticleListItem & {
  publishTime?: string;
  excerpt?: string;
  searchText: string;
};

function decodeBasicEntities(input: string): string {
  return input
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'");
}

function htmlToText(html: string): string {
  if (!html) return '';
  const withoutTags = html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ');

  return decodeBasicEntities(withoutTags).replace(/\s+/g, ' ').trim();
}

function normalizeChineseDateToIso(raw: string): string {
  const m = raw.match(
    /(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?(?:\s+(\d{1,2}:\d{2}))?/
  );
  if (!m) return raw.trim();

  const y = m[1];
  const mm = String(Number(m[2])).padStart(2, '0');
  const dd = String(Number(m[3])).padStart(2, '0');
  const t = m[4] ? ` ${m[4]}` : '';
  return `${y}-${mm}-${dd}${t}`;
}

function extractPublishTimeFromContent(contentHtml: string): string | undefined {
  const text = htmlToText(contentHtml);

  const m1 = text.match(
    /(?:发表时间|发布时间)\s*[:：]\s*([0-9]{4}[-/.][0-9]{1,2}[-/.][0-9]{1,2}(?:\s+\d{1,2}:\d{2})?)/
  );
  if (m1?.[1]) return m1[1].replace(/[/.]/g, '-').trim();

  const m2 = text.match(
    /(?:发表时间|发布时间)\s*[:：]\s*([0-9]{4}\s*年\s*[0-9]{1,2}\s*月\s*[0-9]{1,2}\s*日?(?:\s+\d{1,2}:\d{2})?)/
  );
  if (m2?.[1]) return normalizeChineseDateToIso(m2[1]);

  return undefined;
}

function buildExcerptFromContent(contentHtml: string, maxLen = 90): string | undefined {
  const text = htmlToText(contentHtml)
    .replace(/(?:发表时间|发布时间)\s*[:：][\s\S]*$/i, '')
    .trim();

  if (!text) return undefined;
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen)}…`;
}

function normalizeForCompareText(text: string): string {
  return htmlToText(text)
    .replace(/[\s\u3000]+/g, '')
    .replace(/[：:，,。.!！?？、“”"'‘’（）()【】[\]—\-_]/g, '')
    .toLowerCase();
}

const ArticleList: React.FC<Props> = ({
  dataId,
  data,
  pageSize = 12,
  showSearch = true,
  emptyText = '暂无内容',
}) => {
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const normalized: NormalizedItem[] = useMemo(() => {
    return (data || []).map((item) => {
      const contentHtml = (item as any).ispdf ? '' : (item.content || '');
      const publishTime = extractPublishTimeFromContent(contentHtml);
      let excerpt = buildExcerptFromContent(contentHtml);
      if (excerpt && normalizeForCompareText(excerpt) === normalizeForCompareText(item.title)) {
        excerpt = undefined;
      }
      const searchText = `${item.title || ''} ${excerpt || ''}`.toLowerCase();
      return { ...item, publishTime, excerpt, searchText };
    });
  }, [data]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return normalized;
    return normalized.filter((item) => item.searchText.includes(q));
  }, [normalized, query]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const pageItems = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const navigateToArticle = useCallback(
    (articleId: number) => {
      history.push(`/article/${dataId}/${articleId}`);
    },
    [history, dataId]
  );

  return (
    <div className="article-list">
      {showSearch && (
        <div className="article-list__toolbar">
          <Input
            value={query}
            allowClear
            placeholder="搜索标题/摘要"
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="article-list__count">共 {filtered.length} 条</div>
        </div>
      )}

      {filtered.length === 0 ? (
        <Empty description={emptyText} />
      ) : (
        <>
          <List
            className="article-list__list"
            dataSource={pageItems}
            renderItem={(item) => (
              <List.Item
                className="article-list__item"
                onClick={() => navigateToArticle(item.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigateToArticle(item.id);
                }}
                tabIndex={0}
                role="button"
                aria-label={`打开文章：${item.title}`}
              >
                <div className="article-list__row">
                  <div className="article-list__title">
                    <Typography.Text>{item.title}</Typography.Text>
                  </div>
                  {item.publishTime && <div className="article-list__date">{item.publishTime}</div>}
                </div>
                <div className="article-list__more">
                  <Typography.Link
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigateToArticle(item.id);
                    }}
                  >
                    阅读全文
                  </Typography.Link>
                </div>
              </List.Item>
            )}
          />

          <div className="article-list__pagination">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={filtered.length}
              showSizeChanger={false}
              showQuickJumper
              onChange={(p) => setPage(p)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ArticleList;
